import { IVector3 } from '@seddi/math-v2';
import {
  BufferAttribute,
  BufferGeometry,
  InterleavedBufferAttribute,
  Intersection,
  Mesh,
  Object3D,
  Plane,
  Raycaster,
  Vector2,
  Vector3,
} from '@seddi/three';

import { Viewport } from '../../../core/viewport';
import Camera from '../camera';

export enum ObjectTypes {
  LineSegments = 'LineSegments',
  Mesh = 'Mesh',
}

interface IHitDataFromPointer {
  point: Vector3;
  entity: string;
  piece: string;
  plane: Plane;
}

interface SphereHit {
  distance: number;
  entity: string;
  index: number;
  normalizedDistance: number;
  offsetVector3: IVector3;
}

const raycaster = new Raycaster();
const auxVector = new Vector3();

/** Gets pixel screen position from scene 3D point
 * @param position Vector to be projected on the viewport
 * @param camera Camera to obtain the normalized position
 * @param viewport Viewport that contains the width and height information
 * @returns Vector resulted by the projection
 */
export const projectToScreenSpace = (
  position: Vector3,
  camera: Camera,
  viewport: Viewport,
): Vector3 => {
  const { width, height } = viewport;
  const projected = getNormalizedScreenPosition(position, camera);

  projected.x = Math.round((projected.x + 1) * (width / 2));
  projected.y = Math.round((-projected.y + 1) * (height / 2));
  projected.z = Math.round(projected.z);
  return projected;
};

/** Gets hit data with respect to the given point, camera, renderables and object type
 * @param normalizedScreenPoint Vector to be projected on the viewport
 * @param camera To raycast from the normalized screen point
 * @param renderables Map of objects to hit
 * @param type Object type
 * @returns Hit data that contains piece, point, plane and entity ID
 */
export const getHitDataFromPointer = (
  normalizedScreenPoint: Vector2,
  camera: Camera,
  renderables: Map<string, Object3D>,
  type = ObjectTypes.Mesh,
): IHitDataFromPointer => {
  const [hit] = getHitFromPointer(normalizedScreenPoint, camera, renderables, type);
  if (hit?.point && hit?.object) {
    raycaster.setFromCamera(normalizedScreenPoint, camera);

    const {
      object: {
        parent: {
          name: entity,
          userData: { garmentPiece },
        },
      },
      point,
    } = hit;
    const plane = getPlaneFromCamera(point, camera);
    return { entity, piece: garmentPiece._id, plane, point };
  }
  return undefined;
};

/** Gets a plane perpendicular to camera on a scene point
 * @param position Vector to translate the plane
 * @param camera Camera to obtain the world directon of
 * @returns Plane perpendicular to camera on a scene point
 */
export const getPlaneFromCamera = (position: Vector3, camera: Camera): Plane => {
  camera.getWorldDirection(auxVector);
  const plane = new Plane(auxVector);
  plane.translate(position);
  return plane;
};

/** Gets the 3D position of screen pointer
 * @param plane Plane perpendicular to the camera
 * @param normalizedScreenPoint Normalized pointer position
 * @param camera To raycast from the normalized screen point
 * @returns Vector indicating the hit point
 */
export const getPlanePointerHit = (
  plane: Plane,
  normalizedScreenPoint: Vector2,
  camera: Camera,
): Vector3 => {
  raycaster.setFromCamera(normalizedScreenPoint, camera);
  const { ray } = raycaster;

  const hitPoint = new Vector3();
  ray.intersectPlane(plane, hitPoint);

  return hitPoint;
};

/** Gets 2D screen position from scene 3D point
 * @param position Vector to project onto the screen
 * @param camera Camera to project the vector with
 * @returns Normalized screen position corresponding to the input vector
 */
const getNormalizedScreenPosition = (position: Vector3, camera: Camera): Vector3 => {
  const projected = position.clone().project(camera);
  return projected;
};

/** Gets the closest point on entity from camera ray
 * @param entityName String indicating the entity name
 * @param position To calculate the closest intersection
 * @param camera To project the given position onto the screen
 * @param renderables Map of objects to hit
 * @returns Hit point or given position
 */
export const getEntityIntersection = (
  entityName: string,
  position: Vector3,
  camera: Camera,
  renderables: Map<string, Object3D>,
): Vector3 => {
  const hits = getHitFromPosition(position, camera, renderables);
  const hit = hits.find(({ object }) => object.name === entityName);
  return hit?.point || position;
};

/** Gets scene object form scene point
 * @param position To calculate the intersection
 * @param camera To project the given position onto the screen
 * @param renderables Map of objects to hit
 * @param type Object type
 * @returns Array of intersections from the position
 */
export const getHitFromPosition = (
  position: Vector3,
  camera: Camera,
  renderables: Map<string, Object3D>,
  type = ObjectTypes.Mesh,
): Intersection[] => {
  const { x, y } = getNormalizedScreenPosition(position, camera);
  const normalizedScreenPoint = new Vector2(x, y);

  return getHitFromPointer(normalizedScreenPoint, camera, renderables, type);
};

/** Gets scene object form screen point
 * @param normalizedScreenPoint Normalized pointer position
 * @param camera To raycast from the normalized screen point
 * @param renderables Map of objects to hit
 * @param type Object type
 * @returns Array of intersections from the pointer
 */
export const getHitFromPointer = (
  normalizedScreenPoint: Vector2,
  camera: Camera,
  renderables: Map<string, Object3D>,
  type = ObjectTypes.Mesh,
): Intersection[] => {
  raycaster.setFromCamera(normalizedScreenPoint, camera);
  const selectables = Array.from(renderables.values()) as Object3D[];
  const hits = raycaster
    .intersectObjects(selectables)
    .filter(({ object }) => (type ? object.type === type : true));
  return hits;
};

const buildAdjacencyList = (geometry: BufferGeometry): Map<number, number[]> => {
  const adjacencyList = new Map<number, number[]>();

  for (let i = 0; i < geometry.index!.count; i += 3) {
    const [a, b, c] = [
      geometry.index!.array[i],
      geometry.index!.array[i + 1],
      geometry.index!.array[i + 2],
    ];

    if (!adjacencyList.has(a)) adjacencyList.set(a, []);
    if (!adjacencyList.has(b)) adjacencyList.set(b, []);
    if (!adjacencyList.has(c)) adjacencyList.set(c, []);

    adjacencyList.get(a)!.push(b, c);
    adjacencyList.get(b)!.push(a, c);
    adjacencyList.get(c)!.push(a, b);
  }

  // Remove duplicate neighbors
  adjacencyList.forEach((neighbors) => {
    const uniqueNeighbors = Array.from(new Set(neighbors));
    neighbors.length = 0;
    neighbors.push(...uniqueNeighbors);
  });

  return adjacencyList;
};

const getTopologicallyConnectedVertices = (
  startIndex: number,
  geometry: BufferGeometry,
  radius: number,
  position: BufferAttribute | InterleavedBufferAttribute,
): Set<number> => {
  const visited = new Set<number>();
  const queue = [{ index: startIndex, surfaceDistance: 0 }];
  const surfaceDistances = new Map<number, number>(); // Store cumulative surface distances

  const adjacencyList = buildAdjacencyList(geometry);

  visited.add(startIndex);
  surfaceDistances.set(startIndex, 0);

  while (queue.length > 0) {
    const { index: currentIndex, surfaceDistance: currentSurfaceDistance } = queue.shift()!;
    const i3 = currentIndex * 3;
    const currentPosition = new Vector3(
      position.array[i3],
      position.array[i3 + 1],
      position.array[i3 + 2],
    );

    // Directly access neighbors from the adjacency list
    const neighbors = adjacencyList.get(currentIndex) || [];

    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        const ni3 = neighbor * 3;
        const neighborPosition = new Vector3(
          position.array[ni3],
          position.array[ni3 + 1],
          position.array[ni3 + 2],
        );

        // Calculate surface distance from the current vertex to the neighbor
        const edgeDistance = currentPosition.distanceTo(neighborPosition);
        const newSurfaceDistance = currentSurfaceDistance + edgeDistance;

        // Only proceed if the cumulative surface distance is within the radius
        if (newSurfaceDistance <= radius) {
          visited.add(neighbor);
          surfaceDistances.set(neighbor, newSurfaceDistance);
          queue.push({ index: neighbor, surfaceDistance: newSurfaceDistance });
        }
      }
    }
  }

  return visited;
};

/** Gets all vertices within a radius from specific point
 * @param center Point to find vertices around
 * @param radius To the center for hit success
 * @param renderables Map of objects to hit
 * @param type Object type
 * @param onlyConnected Flag for only gettting the topologically connected vertices
 * @returns Array of intersections within the radius
 */
export const getVerticesWithinRadius = (
  center: IVector3,
  radius: number,
  renderables: Object3D[],
  type = ObjectTypes.Mesh,
  onlyConnected = false, // Optional flag to filter only connected vertices
): SphereHit[] => {
  const meshes = renderables.filter((renderable) => renderable.type === type) as Mesh[];
  const v_ = new Vector3(0, 0, 0);

  const verticesInRadius: SphereHit[] = [];

  // Step 1: Find all vertices within the radius across all meshes
  meshes.forEach((mesh) => {
    const geometry = mesh.geometry as BufferGeometry;
    const position = geometry.attributes.position;

    for (let i = 0; i < position.count; i++) {
      const i3 = i * 3;
      v_.x = position.array[i3] - center.x;
      v_.y = position.array[i3 + 1] - center.y;
      v_.z = position.array[i3 + 2] - center.z;
      const distance = v_.length();

      if (distance <= radius) {
        const hit: SphereHit = {
          distance,
          entity: mesh.parent.name,
          index: i,
          normalizedDistance: distance / radius,
          offsetVector3: { x: v_.x, y: v_.y, z: v_.z },
        };
        verticesInRadius.push(hit);
      }
    }
  });

  if (verticesInRadius.length === 0) return []; // Early exit if no vertices are within the radius

  // Step 2: Find the single closest hit
  const closestHit = verticesInRadius.reduce((closest, hit) =>
    hit.distance < closest.distance ? hit : closest,
  );

  // Step 3: If onlyConnected is true, get only the topologically connected vertices
  if (onlyConnected) {
    const closestMesh = meshes.find((mesh) => mesh.parent.name === closestHit.entity);
    if (!closestMesh) return [];

    const geometry = closestMesh.geometry as BufferGeometry;
    const position = geometry.attributes.position;

    const connectedIndices = getTopologicallyConnectedVertices(
      closestHit.index,
      geometry,
      radius,
      position,
    );

    // Filter verticesInRadius to include only connected vertices
    return verticesInRadius.filter((hit) => connectedIndices.has(hit.index));
  }

  // If onlyConnected is false, return all vertices within the radius
  return verticesInRadius;
};

/** Checks if a point is visible on the scene
 * @param entityName String indicating the entity name
 * @param position To calculate the normalized screen position
 * @param camera To project the given position onto the screen
 * @param renderables Map of objects to hit
 * @returns Flag indicating the point is visible
 */
export const getPointVisibility = (
  entityName: string,
  position: Vector3,
  camera: Camera,
  renderables: Map<string, Object3D>,
): boolean => {
  const normalizedScreenPoint = getNormalizedScreenPosition(position, camera);
  const [hit] = getHitFromPosition(normalizedScreenPoint, camera, renderables);

  // check if the first hit is the object we are testing for
  return hit?.object.name === entityName;
};
